import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    inject,
    type OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { type MenuItem, type MenuItemCommandEvent, OpenngIcons } from '@openng/optimus-ui/api';
import { AvatarModule } from '@openng/optimus-ui/avatar';
import { ButtonModule } from '@openng/optimus-ui/button';
import { type CheckboxChangeEvent, CheckboxModule } from '@openng/optimus-ui/checkbox';
import { DividerModule } from '@openng/optimus-ui/divider';
import { IconFieldModule } from '@openng/optimus-ui/iconfield';
import { InputIconModule } from '@openng/optimus-ui/inputicon';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { MenuModule } from '@openng/optimus-ui/menu';
import { MultiSelectModule } from '@openng/optimus-ui/multiselect';
import { type Popover, PopoverModule } from '@openng/optimus-ui/popover';
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import type { OptionChangedEvent, RowPreparedEvent } from 'devextreme/ui/data_grid';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { xor } from 'lodash-es';
import { debounceTime, distinctUntilChanged, lastValueFrom } from 'rxjs';
import { DateFromNowPipe } from '../../pipes/date-from-now.pipe';

import type { LineageLegend } from '@app/shared/components/lineage/lineage.model';
import {
    NOTIFICATION_TYPES,
    OBJECT_DISPLAY_NAMES,
    OBJECT_INFO,
    OBJECTS,
} from '@app/shared/constants/shared.constant';
import type { DxDataGridColumn } from '@app/shared/interface/devextreme.interface';
import type { NotificationAdapter } from '@app/shared/models/notification.model';
import { UniqueRgbColorPipe } from '@app/shared/pipes/unique-rgb-color.pipe';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { NotificationService } from '@app/shared/services/notification.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { WindowService } from '@app/shared/services/window.service';
import { Helpers } from '@app/shared/utils/helpers';
import { RouterHelper } from '@app/shared/utils/router-helper';

enum MENU_ITEMS {
    ALL = 'All',
    READ = 'Read',
    UNREAD = 'Unread',
    STARRED = 'Starred',
}

enum SORT_BY_ORDER {
    ASC = 'asc',
    DESC = 'desc',
}

interface FilterParams {
    text?: string;
    sortByOrder?: SORT_BY_ORDER;
    objectType?: string[];
    filterType?: Exclude<MENU_ITEMS, MENU_ITEMS.ALL>;
    event?: string[];
}

export const TEMPLATE_FILTER_TYPE = {
    SIMULATION_STATUS_CHANGE: 'SimulationStatusChange',
    REVIEW_STATUS_CHANGE: 'ReviewStatusChange',
    TRIGGER_ALERT_CREATED: 'TriggerAlertCreated',
};

export const LINEAGE_OBJECTS = [
    OBJECTS.DATA_TABLE,
    OBJECTS.GLOBAL_FUNCTION,
    OBJECTS.REPORT,
    OBJECTS.PROMPT,
    OBJECTS.FOUNDATION_MODEL,
    OBJECTS.RAG,
    OBJECTS.PIPELINE,
];

@Component({
    selector: 'cor-notification',
    templateUrl: './notification.component.html',
    styleUrls: ['./notification.component.scss'],
    imports: [
        DividerModule,
        MenuModule,
        SelectButtonModule,
        InputTextModule,
        IconFieldModule,
        InputIconModule,
        ReactiveFormsModule,
        FormsModule,
        MultiSelectModule,
        CheckboxModule,
        DxDataGridModule,
        DxTemplateDirective,
        DateFromNowPipe,
        TooltipModule,
        PopoverModule,
        AvatarModule,
        UniqueRgbColorPipe,
        ButtonModule,
        NgClass,
        NgStyle,
        NgTemplateOutlet,
    ],
    host: {
        '(window:resize)': 'onWindowResize()',
    },
})
export class NotificationComponent implements OnInit {
    protected readonly dataGrid = viewChild.required(DxDataGridComponent);

    private readonly menuPopoverEl = viewChild<Popover>('menuPopover');
    protected readonly objectFilterValues = signal(
        LINEAGE_OBJECTS.map(obj => ({
            name: OBJECT_DISPLAY_NAMES[obj],
            key: obj,
            color: OBJECT_INFO[obj].color,
        })),
    );

    protected readonly isSidebarOpen = signal(true);

    /**
     * For smaller screen resolutions display filter btn which contains general and event filters
     * instead of the sidebar
     */
    protected readonly isMenuPopoverVisible = signal(false);

    protected readonly activeMenuItem = signal<MenuItem>(null);

    protected readonly SORT_BY_ORDER = SORT_BY_ORDER;

    protected readonly isSearchFieldEnabled = signal(false);

    protected readonly isFiltersApplied = signal(false);

    protected readonly selectedNotificationKeys = signal([]);

    readonly unreadNotificationCount = signal('');

    readonly starredNotificationCount = signal('');

    readonly areAllNotificationsSelected = signal(false);

    protected readonly eventFilters = signal<MenuItem[]>([
        {
            label: 'Event Filters',
            items: [
                {
                    label: 'Jobs',
                    checked: false,
                    value: TEMPLATE_FILTER_TYPE.SIMULATION_STATUS_CHANGE,
                },
                {
                    label: 'Approval',
                    checked: false,
                    value: TEMPLATE_FILTER_TYPE.REVIEW_STATUS_CHANGE,
                },
                {
                    label: 'Monitoring Alert',
                    checked: false,
                    value: TEMPLATE_FILTER_TYPE.TRIGGER_ALERT_CREATED,
                },
            ],
        },
    ]);

    protected readonly menuItems = computed(() => [
        {
            label: 'General',
            items: [
                {
                    label: MENU_ITEMS.ALL,
                    value: MENU_ITEMS.ALL,
                    icon: OpenngIcons.INBOX,
                    styleClass:
                        this.activeMenuItem()?.label === (MENU_ITEMS.ALL as string)
                            ? 'active-menu-item'
                            : '',
                    command: (event: MenuItemCommandEvent) => this.updateMenuFilter(event.item),
                },
                {
                    label: MENU_ITEMS.READ,
                    value: MENU_ITEMS.READ,
                    icon: OpenngIcons.FILE_CHECK,
                    styleClass:
                        this.activeMenuItem()?.label === (MENU_ITEMS.READ as string)
                            ? 'active-menu-item'
                            : '',
                    command: (event: MenuItemCommandEvent) => this.updateMenuFilter(event.item),
                },
                {
                    label: MENU_ITEMS.UNREAD,
                    value: MENU_ITEMS.UNREAD,
                    icon: OpenngIcons.FILE,
                    badge: this.unreadNotificationCount(),
                    styleClass:
                        this.activeMenuItem()?.label === (MENU_ITEMS.UNREAD as string)
                            ? 'active-menu-item'
                            : '',
                    command: (event: MenuItemCommandEvent) => this.updateMenuFilter(event.item),
                },
                {
                    label: MENU_ITEMS.STARRED,
                    value: MENU_ITEMS.STARRED,
                    icon: OpenngIcons.STAR,
                    badge: this.starredNotificationCount(),
                    styleClass:
                        this.activeMenuItem()?.label === (MENU_ITEMS.STARRED as string)
                            ? 'active-menu-item'
                            : '',
                    command: (event: MenuItemCommandEvent) => this.updateMenuFilter(event.item),
                },
            ],
        },
    ]);

    protected readonly columns = signal<DxDataGridColumn[]>([
        {
            dataField: 'name',
            cellTemplate: 'nameTemplate',
        },
        {
            dataField: 'timeStamp',
            cellTemplate: 'timeTemplate',
        },
    ]);

    protected notificationDatasource: DataSource;

    /**
     * Holds filter properties
     */
    protected form = new FormGroup({
        searchText: new FormControl<string>(''),
        objectFilters: new FormControl<LineageLegend[]>([]),
        sortByOrder: new FormControl<SORT_BY_ORDER>(SORT_BY_ORDER.DESC),
        eventFilters: new FormControl<MenuItem[]>([]),
        isStarred: new FormControl<boolean>(null),
        isViewed: new FormControl<boolean>(null),
    });

    protected readonly pageSize = computed(
        () => this.appConfigService.appConfig().paginationItemsPerPage,
    );

    protected readonly notificationIcons: {
        [N in NOTIFICATION_TYPES as string]: string;
    } = {
        [NOTIFICATION_TYPES.FAILURE]: 'pi pi-times-circle tw:text-red-500',
        [NOTIFICATION_TYPES.INFO]: 'pi pi-check-circle tw:text-primary',
        [NOTIFICATION_TYPES.SUCCESS]: 'pi pi-check-circle tw:text-green-500',
    };

    private readonly notificationService = inject(NotificationService);
    private readonly appConfigService = inject(AppConfigService);
    private readonly toast = inject(ToastNotificationService);
    private readonly windowService = inject(WindowService);
    private readonly destroyRef = inject(DestroyRef);

    ngOnInit() {
        this.activeMenuItem.set(this.menuItems().at(0).items[0]);

        this.form.controls.searchText.valueChanges
            .pipe(distinctUntilChanged(), debounceTime(300), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.reloadNotifications();
            });

        // listen to notification-count-changes and reload the data-table
        this.notificationService.unreadNotificationCount$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(count => {
                this.unreadNotificationCount.set(count);
                if (this.notificationDatasource) {
                    this.notificationDatasource.reload();
                }
            });

        this.onWindowResize();
        this.getStarredNotificationCount();
        this.reloadNotifications();
    }

    /**
     * This function is called whenever user applies/modifies/removes any of the filters
     * and updates the notification table.
     */
    protected reloadNotifications(): void {
        const filters: FilterParams = {};
        if (this.form.controls.searchText.value) {
            filters.text = this.form.controls.searchText.value;
        }
        if (this.form.controls.eventFilters.value.length > 0) {
            filters.event = this.form.controls.eventFilters.value.map(e => e.label);
        }
        if (this.form.controls.sortByOrder.value === SORT_BY_ORDER.ASC) {
            filters.sortByOrder = SORT_BY_ORDER.ASC;
        }

        const objectFiltersArray = this.form.controls.objectFilters.value || [];
        if (objectFiltersArray.length > 0) {
            filters.objectType = objectFiltersArray.map((e: LineageLegend) => e.key);
        }
        if (this.activeMenuItem()?.value !== MENU_ITEMS.ALL) {
            filters.filterType = this.activeMenuItem()?.value;
        }

        this.updateNotificationStore(filters);
        this.isFiltersApplied.set(!Helpers.isEmpty(filters));
        this.selectedNotificationKeys.set([]);
        this.areAllNotificationsSelected.set(false);
    }

    private updateNotificationStore(params: FilterParams) {
        this.notificationDatasource = new DataSource({
            store: new CustomStore({
                key: 'id',
                load: loadOptions => {
                    // The API expects template values while the menu stores translated labels.
                    const templateVal = this.eventFilters()[0]
                        .items.filter(e => params.event?.includes(e.label))
                        .map(e => e.value);

                    return lastValueFrom(
                        this.notificationService.getDataList({
                            skip: loadOptions.skip,
                            take: loadOptions.take,
                            isViewed: this.form.controls.isViewed.value,
                            isStarred: this.form.controls.isStarred.value,
                            sortByOrder: params.sortByOrder,
                            objectType: params.objectType,
                            // The string present in API response has double-quotes in escape character
                            // format. So we need to send the double-qoutes back in the same format
                            keyword: params.text?.replaceAll('"', '\\"'),
                            template: templateVal,
                        }),
                    ).then(response => {
                        return { data: response.result, totalCount: response.pageInfo.total };
                    });
                },
            }),
        });
    }

    private getStarredNotificationCount() {
        this.notificationService
            .starredNotificationCount()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
                const count = response.result.starred || 0;
                if (count) {
                    this.starredNotificationCount.set(count > 99 ? '99+' : count.toString());
                } else {
                    this.starredNotificationCount.set('');
                }
            });
    }

    protected redirectToNotification(notification: NotificationAdapter): void {
        const notificationRedirectUrl =
            notification.messageData.uiPath ??
            RouterHelper.buildRoute(
                Helpers.convertNotificationDataToUIRouteSchema(notification.messageData),
            );
        if (notificationRedirectUrl) {
            this.windowService.openInNewTab(notificationRedirectUrl);
        }
    }

    protected markAsReadAndRedirect(notification: NotificationAdapter): void {
        this.notificationService
            .markNotificationAsRead(notification)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
                if (response.result?.isViewed) {
                    notification.isViewed = true;
                    this.notificationDatasource.reload();
                }
                this.redirectToNotification(notification);
            });
    }

    protected toggleMarkAsRead(read: boolean) {
        const payload = {
            notifications: this.selectedNotificationKeys(),
            read,
        };
        this.notificationService
            .toggleNotificationsAsRead(payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
                this.dataGrid().instance.clearSelection();
                this.areAllNotificationsSelected.set(false);
                this.notificationService.refreshUnreadNotificationCount();
                this.notificationDatasource.reload();
                this.toast.showSuccess(response.result.message);
            });
    }

    protected toggleStarred(id: number, isStarred: boolean, event?: Event): void {
        const toBeStarred = !isStarred;
        event?.stopPropagation();
        this.notificationService
            .toggleNotificationStarred(id, { starred: toBeStarred })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(_ => {
                this.getStarredNotificationCount();
                this.notificationDatasource.reload();
                this.toast.showSuccess(
                    toBeStarred
                        ? 'Starred notification successfully'
                        : 'Unstarred notification successfully',
                );
            });
    }

    protected onDateSortChange() {
        this.form.controls.sortByOrder.setValue(
            this.form.controls.sortByOrder.value === SORT_BY_ORDER.DESC
                ? SORT_BY_ORDER.ASC
                : SORT_BY_ORDER.DESC,
        );
        this.reloadNotifications();
    }

    protected onSelectAllChange(event: CheckboxChangeEvent) {
        // Only proceed if this is a user-initiated change
        if (!event.originalEvent) {
            return; // Programmatic change; ignore it.
        }

        if (event.checked) {
            this.dataGrid().instance.selectAll();
        } else {
            this.dataGrid().instance.clearSelection();
        }
    }

    protected onSelectedRowsChange() {
        this.selectedNotificationKeys.set(this.dataGrid().instance.getSelectedRowKeys());

        /**
         * Update the Select All btn
         * 1. 'Select All' should be checked when all rows are selected
         * 2. 'Select All' should be unchecked when one of the visible rows is unchecked
         */
        const visibleRowKeys = this.dataGrid()
            .instance.getVisibleRows()
            .map(e => e.key);
        const selectedRowKeys = this.selectedNotificationKeys();

        this.areAllNotificationsSelected.set(xor(visibleRowKeys, selectedRowKeys).length === 0);
    }

    protected onEventFilterChange(checked: boolean, eventFilterLabel: string) {
        const currentFilters = this.eventFilters();
        const selectedEvent = currentFilters[0]?.items?.find(e => e.value === eventFilterLabel);

        if (!selectedEvent) {
            return;
        }

        // Update the checked state in the signal
        selectedEvent.checked = checked;

        // Update the signal to trigger change detection
        this.eventFilters.set([...currentFilters]);

        // Update the form
        if (checked) {
            const currentFormEvents = this.form.controls.eventFilters.value || [];
            if (!currentFormEvents.some((e: MenuItem) => e.value === selectedEvent.value)) {
                this.form.controls.eventFilters.setValue([...currentFormEvents, selectedEvent]);
            }
        } else {
            const currSelectedEvents = this.form.controls.eventFilters.value.filter(
                (e: MenuItem) => e.value !== selectedEvent.value,
            );
            this.form.controls.eventFilters.setValue(currSelectedEvents);
        }
        this.reloadNotifications();
    }

    private updateMenuFilter(item: MenuItem) {
        this.form.controls.isStarred.setValue(item.value === MENU_ITEMS.STARRED ? true : null);
        if (item.value === MENU_ITEMS.ALL || item.value === MENU_ITEMS.STARRED) {
            this.form.controls.isViewed.setValue(null);
        } else if (item.value === MENU_ITEMS.READ) {
            this.form.controls.isViewed.setValue(true);
        } else if (item.value === MENU_ITEMS.UNREAD) {
            this.form.controls.isViewed.setValue(false);
        }
        this.activeMenuItem.set(item);
        this.reloadNotifications();
    }

    protected clearFilters(): void {
        this.form.patchValue(
            {
                searchText: '',
                objectFilters: [],
                sortByOrder: SORT_BY_ORDER.DESC,
                eventFilters: [],
                isStarred: null,
                isViewed: null,
            },
            { emitEvent: false },
        );
        this.activeMenuItem.set(this.menuItems().at(0).items[0]);
        this.eventFilters()[0].items.forEach(e => {
            e.checked = false;
        });

        this.reloadNotifications();
    }

    protected toggleSidebar() {
        this.isSidebarOpen.update(val => !val);
    }

    /**
     * Deselect all rows when page-index or page-size is changed
     */
    protected onGridOptionChange(event: OptionChangedEvent) {
        if (['paging.pageIndex', 'paging.pageSize'].includes(event.fullName)) {
            this.areAllNotificationsSelected.set(false);
            this.dataGrid().instance.clearSelection();
        }
    }

    protected rowPrepared(e: RowPreparedEvent) {
        if (e.rowType === 'data' && e.data.isViewed) {
            e.rowElement.style.fontWeight = '400';
            e.rowElement.style.backgroundColor = 'rgb(241 245 249)';
            e.rowElement.style.opacity = '0.8';
        }
    }

    protected getInitials(name: string): string {
        return Helpers.getInitialsFromName(name);
    }

    protected isTypeofLineageLegendObject(objectType: OBJECTS): boolean {
        return LINEAGE_OBJECTS.includes(objectType);
    }

    protected getObjectTypeStyle(objectType: string) {
        const color = OBJECT_INFO[objectType as OBJECTS]?.color;
        return {
            color,
            background: color + 20,
            borderColor: color + 80,
        };
    }

    /**
     * We are displaying filter btn which contains general and event filters for smaller screen resolutions
     * instead of the sidebar. Toggle filter-btn/sidebar based on the window width.
     *
     * Also, Hide popover popup when window size changes for good UX.
     */
    onWindowResize() {
        if (this.menuPopoverEl()) {
            this.menuPopoverEl().hide();
        }
        if (window.innerWidth < 992) {
            this.isSidebarOpen.set(false);
            this.isMenuPopoverVisible.set(true);
        } else {
            this.isSidebarOpen.set(true);
            this.isMenuPopoverVisible.set(false);
        }
    }
}
